'use client';

import { useCallback, useEffect, useState } from 'react';

export type AuthStage = 'choose' | 'kernel' | 'local' | 'error';

export interface AuthOverlayProps {
	/** Starting stage. Defaults to 'choose'. */
	initialStage?: AuthStage;
	/** Pre-fill the kernel URL input. */
	defaultKernel?: string;
	/** Error message shown in the `error` stage. */
	errorMessage?: string;
	/**
	 * Called when the user submits a kernel URL from the `kernel` stage.
	 * The component validates the URL is parseable; the host is responsible
	 * for the actual handoff (e.g. redirect to platform `/auth/kernel-sign-in`).
	 * Throw or return a rejected promise to push the overlay into the `error`
	 * stage with the thrown message.
	 */
	onSubmitKernel?: (url: string) => void | Promise<void>;
	/** Optional sign-in click handler (choose-stage primary action). */
	onSignIn?: () => void;
	/** Optional "Proceed offline" handler from the `local` stage. */
	onProceedLocal?: () => void;
	/** Optional dismiss handler (rendered as the foot Skip button when provided). */
	onDismiss?: () => void;
	/** Optional release/version string in the header. */
	version?: string;
}

/**
 * AuthOverlay — kernel-connect flow ported from the designer's
 * `khalos-shell.jsx:276-310` prototype. Stages: choose / kernel / local / error.
 *
 * This component is intentionally NOT a workspace picker — workspace selection
 * is a separate `/auth/choose` screen. AuthOverlay is the entry point for
 * unauthenticated devices that need a kernel URL before sign-in can proceed.
 */
export function AuthOverlay({
	initialStage = 'choose',
	defaultKernel = '',
	errorMessage,
	onSubmitKernel,
	onSignIn,
	onProceedLocal,
	onDismiss,
	version,
}: AuthOverlayProps) {
	const [stage, setStage] = useState<AuthStage>(initialStage);
	const [kernelUrl, setKernelUrl] = useState(defaultKernel);
	const [localError, setLocalError] = useState<string | null>(errorMessage ?? null);
	const [submitting, setSubmitting] = useState(false);

	useEffect(() => {
		if (errorMessage) {
			setLocalError(errorMessage);
			setStage('error');
		}
	}, [errorMessage]);

	const handleConnect = useCallback(async () => {
		const raw = kernelUrl.trim();
		if (!raw) {
			setLocalError('Enter a kernel URL.');
			setStage('error');
			return;
		}
		const withScheme = /^https?:\/\//.test(raw) ? raw : `https://${raw}`;
		try {
			new URL(withScheme);
		} catch {
			setLocalError('That does not look like a valid URL.');
			setStage('error');
			return;
		}

		setSubmitting(true);
		setLocalError(null);
		try {
			await onSubmitKernel?.(withScheme);
		} catch (err) {
			setLocalError(err instanceof Error ? err.message : 'Could not reach that kernel.');
			setStage('error');
		} finally {
			setSubmitting(false);
		}
	}, [kernelUrl, onSubmitKernel]);

	return (
		<div className="auth-overlay" role="dialog" aria-modal="true" aria-labelledby="auth-overlay-title">
			<div className="auth-card khal-anim-fade-up">
				<div className="auth-head">
					<KIcon size={18} />
					<span className="auth-title" id="auth-overlay-title">KhalOS</span>
					{version && <span className="auth-version">{version}</span>}
				</div>

				{stage === 'choose' && (
					<>
						<div className="auth-h">
							<div className="auth-eye">khal://auth</div>
							<h2>Welcome to KhalOS</h2>
							<p>Sign in to your workspace, or connect to a kernel address manually.</p>
						</div>
						<div className="auth-body">
							<button
								type="button"
								className="k-btn k-btn-primary"
								data-testid="auth-sign-in"
								onClick={() => onSignIn?.()}
							>
								Sign in
							</button>
							<button
								type="button"
								className="k-btn k-btn-secondary"
								data-testid="auth-connect-to-kernel"
								onClick={() => setStage('kernel')}
							>
								Connect to kernel
							</button>
						</div>
					</>
				)}

				{stage === 'kernel' && (
					<>
						<div className="auth-h">
							<div className="auth-eye">khal://auth/kernel</div>
							<h2>Kernel URL</h2>
							<p>Enter the address of your KhalOS kernel.</p>
						</div>
						<form
							className="auth-body"
							onSubmit={(e) => {
								e.preventDefault();
								void handleConnect();
							}}
						>
							<label className="k-label" htmlFor="auth-kernel-url">Kernel URL</label>
							<input
								id="auth-kernel-url"
								data-testid="kernel-url-input"
								className="k-input"
								type="text"
								autoComplete="url"
								autoFocus
								value={kernelUrl}
								placeholder="https://your-workspace.example.com"
								onChange={(e) => setKernelUrl(e.target.value)}
							/>
							<div className="auth-row">
								<button
									type="button"
									className="k-btn k-btn-ghost auth-row-back"
									onClick={() => setStage('choose')}
								>
									Back
								</button>
								<button
									type="submit"
									className="k-btn k-btn-primary auth-row-cta"
									data-testid="kernel-url-submit"
									disabled={submitting}
								>
									{submitting ? 'Connecting…' : 'Connect'}
								</button>
							</div>
						</form>
					</>
				)}

				{stage === 'local' && (
					<>
						<div className="auth-h">
							<div className="auth-eye auth-eye-ok">● local kernel</div>
							<h2>Proceed offline</h2>
							<p>
								Run KhalOS against a kernel on this machine. Useful for development or air-gapped
								environments.
							</p>
						</div>
						<div className="auth-body">
							<button
								type="button"
								className="k-btn k-btn-primary"
								onClick={() => onProceedLocal?.()}
							>
								Proceed locally
							</button>
							<button
								type="button"
								className="k-btn k-btn-ghost"
								onClick={() => setStage('choose')}
							>
								Back
							</button>
						</div>
					</>
				)}

				{stage === 'error' && (
					<>
						<div className="auth-h">
							<div className="auth-eye auth-eye-err">connection refused</div>
							<h2>Could not reach kernel</h2>
							<p data-testid="kernel-url-error">
								{localError ?? 'The kernel responded with an error. Verify the address and try again.'}
							</p>
						</div>
						<div className="auth-body">
							<button
								type="button"
								className="k-btn k-btn-primary"
								onClick={() => {
									setLocalError(null);
									setStage('kernel');
								}}
							>
								Retry
							</button>
							<button
								type="button"
								className="k-btn k-btn-ghost"
								onClick={() => {
									setLocalError(null);
									setStage('choose');
								}}
							>
								Back
							</button>
						</div>
					</>
				)}

				<div className="auth-foot">
					<span className="auth-meta">{`stage · ${stage}`}</span>
					{onDismiss && (
						<button type="button" className="k-btn k-btn-ghost auth-skip" onClick={onDismiss}>
							Skip
						</button>
					)}
				</div>
			</div>
		</div>
	);
}

function KIcon({ size = 18, color = 'currentColor' }: { size?: number; color?: string }) {
	return (
		<svg width={size} height={size} viewBox="0 0 24 24" fill="none" aria-hidden>
			<path
				d="M6 4v16M6 12l9-8M6 12l9 8"
				stroke={color}
				strokeWidth="2"
				strokeLinecap="round"
				strokeLinejoin="round"
			/>
		</svg>
	);
}
